import { useEffect, useState } from 'react'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import PowerIcon from '@mui/icons-material/Power'
import PowerOffIcon from '@mui/icons-material/PowerOff'
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'

import { ADMIN_FIELD_LABELS } from '../constants/admin.constants'
import { DeleteOptionDialog } from './DeleteOptionDialog'
import { DropdownOptionForms, type EditableDropdownOption, type NewOptionForm } from './DropdownOptionForms'
import { dropdownApi } from '../../dropdown/services/dropdownApi'
import type { DropdownDependency, DropdownOption } from '../../dropdown/types/dropdown.types'
import type { ApiError, SnackbarState } from '../../../shared/types/common'

type DeleteDialogState = {
  open: boolean
  item: DropdownOption | null
  deps: DropdownDependency[] | null
}

const DEFAULT_NEW_OPTION_FORM: NewOptionForm = {
  display_value: '',
  internal_value: '',
  prefix: '',
  description: '',
  sort_order: '',
  is_active: true,
}

export function DropdownAdminTab() {
  const [selectedField, setSelectedField] = useState('customer')
  const [options, setOptions] = useState<DropdownOption[]>([])
  const [loading, setLoading] = useState(false)
  const [showInactive, setShowInactive] = useState(false)
  const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', severity: 'success' })
  const [editItem, setEditItem] = useState<DropdownOption | null>(null)
  const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState>({ open: false, item: null, deps: null })
  const [newForm, setNewForm] = useState<NewOptionForm>(DEFAULT_NEW_OPTION_FORM)
  const [editForm, setEditForm] = useState<EditableDropdownOption | null>(null)
  const isDisciplineField = selectedField === 'discipline'

  useEffect(() => {
    void fetchOptions()
  }, [selectedField, showInactive])

  async function fetchOptions() {
    setLoading(true)
    try {
      const response = await dropdownApi.getByField(selectedField, !showInactive)
      setOptions(response.data)
    } catch {
      showSnackbar('Failed to load options', 'error')
    } finally {
      setLoading(false)
    }
  }

  async function handleCreate() {
    if (!newForm.display_value || !newForm.internal_value || (selectedField === 'discipline' && !newForm.prefix.trim())) {
      showSnackbar(selectedField === 'discipline' ? 'Display value, internal value, and prefix are required' : 'Display value and internal value are required', 'error')
      return
    }

    try {
      await dropdownApi.create({
        ...newForm,
        field_name: selectedField,
        sort_order: Number(newForm.sort_order) || 0,
      })
      showSnackbar('Option created')
      setNewForm(DEFAULT_NEW_OPTION_FORM)
      await fetchOptions()
    } catch (error) {
      showSnackbar((error as ApiError).response?.data?.message || 'Failed to create', 'error')
    }
  }

  async function handleUpdate() {
    if (!editItem || !editForm) {
      return
    }

    try {
      await dropdownApi.update(editItem.id, {
        ...editForm,
        sort_order: Number(editForm.sort_order) || 0,
      })
      showSnackbar('Option updated')
      setEditItem(null)
      await fetchOptions()
    } catch (error) {
      showSnackbar((error as ApiError).response?.data?.message || 'Failed to update', 'error')
    }
  }

  async function handleToggleActive(item: DropdownOption) {
    try {
      if (item.is_active) {
        await dropdownApi.deactivate(item.id)
        showSnackbar('Option deactivated')
      } else {
        await dropdownApi.reactivate(item.id)
        showSnackbar('Option reactivated')
      }
      await fetchOptions()
    } catch (error) {
      showSnackbar((error as ApiError).response?.data?.message || 'Failed to update', 'error')
    }
  }

  async function openDeleteDialog(item: DropdownOption) {
    try {
      const response = await dropdownApi.getDependencies(item.id)
      setDeleteDialog({ open: true, item, deps: response.data.dependencies })
    } catch {
      setDeleteDialog({ open: true, item, deps: null })
    }
  }

  async function handleDelete() {
    if (!deleteDialog.item) {
      return
    }

    try {
      await dropdownApi.remove(deleteDialog.item.id)
      showSnackbar('Option deleted')
      setDeleteDialog({ open: false, item: null, deps: null })
      await fetchOptions()
    } catch (error) {
      showSnackbar((error as ApiError).response?.data?.message || 'Cannot delete', 'error')
      setDeleteDialog({ open: false, item: null, deps: null })
    }
  }

  function showSnackbar(message: string, severity: SnackbarState['severity'] = 'success') {
    setSnackbar({ open: true, message, severity })
  }

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <Paper sx={{ p: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h6">Manage Dropdown Options</Typography>
            <Typography variant="body2" color="text.secondary">
              Current field: <strong>{ADMIN_FIELD_LABELS[selectedField] || selectedField}</strong>
            </Typography>
          </Box>
          <Chip label="Admin Only" size="small" color="primary" variant="outlined" />
        </Box>
        <Box display="flex" gap={1} mt={2} flexWrap="wrap">
          {Object.entries(ADMIN_FIELD_LABELS).map(([key, label]) => (
            <Button
              key={key}
              size="small"
              variant={selectedField === key ? 'contained' : 'outlined'}
              onClick={() => setSelectedField(key)}
              sx={{ borderRadius: 999, textTransform: 'none', fontWeight: selectedField === key ? 700 : 400 }}
            >
              {label}
            </Button>
          ))}
        </Box>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
          <Typography variant="subtitle1" fontWeight={700}>
            Current Options
          </Typography>
          <Button size="small" variant="outlined" onClick={() => setShowInactive(value => !value)} sx={{ borderRadius: 2, textTransform: 'none' }}>
            {showInactive ? 'Hide Inactive' : 'Show Inactive'}
          </Button>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Display Value</TableCell>
                <TableCell>Internal Value</TableCell>
                {isDisciplineField && <TableCell>Prefix</TableCell>}
                <TableCell>Description</TableCell>
                <TableCell>Order</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Updated</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={isDisciplineField ? 8 : 7} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                    Loading...
                  </TableCell>
                </TableRow>
              ) : options.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isDisciplineField ? 8 : 7} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                    No options found
                  </TableCell>
                </TableRow>
              ) : (
                options.map(option => (
                  <TableRow key={option.id} sx={{ opacity: option.is_active ? 1 : 0.55 }}>
                    <TableCell sx={{ fontWeight: 600 }}>{option.display_value}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, color: '#667085' }}>{option.internal_value}</TableCell>
                    {isDisciplineField && <TableCell sx={{ fontWeight: 700 }}>{option.prefix || '—'}</TableCell>}
                    <TableCell sx={{ maxWidth: 200, fontSize: 13, color: '#667085' }}>{option.description || '—'}</TableCell>
                    <TableCell>{option.sort_order}</TableCell>
                    <TableCell>
                      <Typography variant="caption" fontWeight={700} color={option.is_active ? 'success.main' : 'warning.main'}>
                        {option.is_active ? 'Active' : 'Inactive'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, color: '#667085' }}>
                      {new Date(option.updated_at).toLocaleDateString()}
                      {option.updated_by_name ? ` • ${option.updated_by_name}` : ''}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditItem(option)
                            setEditForm({
                              display_value: option.display_value,
                              internal_value: option.internal_value,
                              prefix: option.prefix || '',
                              description: option.description,
                              sort_order: option.sort_order,
                              is_active: Boolean(option.is_active),
                            })
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={option.is_active ? 'Deactivate' : 'Reactivate'}>
                        <IconButton size="small" sx={{ color: option.is_active ? '#b7791f' : '#198754' }} onClick={() => handleToggleActive(option)}>
                          {option.is_active ? <PowerOffIcon fontSize="small" /> : <PowerIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" sx={{ color: '#b42318' }} onClick={() => openDeleteDialog(option)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <DropdownOptionForms
        editForm={editForm}
        editItem={editItem}
        newForm={newForm}
        onClearNewForm={() => setNewForm(DEFAULT_NEW_OPTION_FORM)}
        onCreate={handleCreate}
        onDiscardEdit={() => setEditItem(null)}
        onUpdate={handleUpdate}
        setEditForm={setEditForm}
        setNewForm={setNewForm}
        selectedField={selectedField}
      />

      <DeleteOptionDialog
        deps={deleteDialog.deps}
        item={deleteDialog.item}
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, item: null, deps: null })}
        onConfirmDelete={handleDelete}
        onDeactivateInstead={() => {
          if (deleteDialog.item) {
            void handleToggleActive(deleteDialog.item)
          }
          setDeleteDialog({ open: false, item: null, deps: null })
        }}
      />

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar(current => ({ ...current, open: false }))}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  )
}
